// Animated ports of the CSS keyframe animations in the designer's handoff
// (store.css / vault.css). Durations and keyframe stops match the originals.

import { useEffect, useRef } from 'react';
import { Animated, Easing } from 'react-native';

// react-native-web has no native driver; native does for transform/opacity.
const NATIVE_DRIVER = false;

/**
 * torchFlicker 1.7s infinite:
 *   0/100% opacity 1  scale 1    rotate 0
 *   25%    opacity .9 scale .96  rotate -2deg
 *   50%    opacity 1  scale 1.05 rotate 2deg
 *   75%    opacity .94 scale .98 rotate -1deg
 */
export function useTorchFlicker() {
  const t = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(t, {
        toValue: 1,
        duration: 1700,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: NATIVE_DRIVER,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [t]);

  const input = [0, 0.25, 0.5, 0.75, 1];
  return {
    opacity: t.interpolate({ inputRange: input, outputRange: [1, 0.9, 1, 0.94, 1] }),
    transform: [
      { scale: t.interpolate({ inputRange: input, outputRange: [1, 0.96, 1.05, 0.98, 1] }) },
      {
        rotate: t.interpolate({
          inputRange: input,
          outputRange: ['0deg', '-2deg', '2deg', '-1deg', '0deg'],
        }),
      },
    ],
  };
}

/** A 0 -> 1 -> 0 infinite breathing value, e.g. haloPulse 1.8s. */
export function useBreathing(duration, enabled = true) {
  const t = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!enabled) return undefined;
    t.setValue(0);
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(t, {
          toValue: 1,
          duration: duration / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: NATIVE_DRIVER,
        }),
        Animated.timing(t, {
          toValue: 0,
          duration: duration / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: NATIVE_DRIVER,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [t, duration, enabled]);
  return t;
}

/**
 * modalShimmer / detailShimmer: a diagonal highlight sweeping across once,
 * 1.2s ease-out after a short delay, fading in at 10% and out after 55%.
 * Returns 0 -> 1 progress; restarts whenever `key` changes.
 */
export function useShimmerSweep(key, delay = 150) {
  const t = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    t.setValue(0);
    const anim = Animated.sequence([
      Animated.delay(delay),
      Animated.timing(t, {
        toValue: 1,
        duration: 1200,
        easing: Easing.out(Easing.ease),
        useNativeDriver: NATIVE_DRIVER,
      }),
    ]);
    anim.start();
    return () => anim.stop();
  }, [t, key, delay]);
  return t;
}

/**
 * modalPop 0.35s: scale .4 -> 1.08 (70%) -> 1, opacity 0 -> 1.
 * headerCompanionPop 0.3s: scale .7 -> 1, opacity 0 -> 1.
 * Replays whenever `key` changes.
 */
export function usePopIn(key, { duration = 350, from = 0.4, overshoot = 1.08 } = {}) {
  const t = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    t.setValue(0);
    const anim = Animated.timing(t, {
      toValue: 1,
      duration,
      easing: Easing.ease,
      useNativeDriver: NATIVE_DRIVER,
    });
    anim.start();
    return () => anim.stop();
  }, [t, key, duration]);

  const scaleStops = overshoot ? [from, overshoot, 1] : [from, (from + 1) / 2, 1];
  return {
    opacity: t.interpolate({ inputRange: [0, 0.7, 1], outputRange: [0, 1, 1] }),
    transform: [
      { scale: t.interpolate({ inputRange: [0, 0.7, 1], outputRange: scaleStops }) },
    ],
  };
}

/**
 * Plays a one-shot 0 -> 1 animation each time `active` turns true, and resets
 * when it turns false. Used for:
 *   chestPulse     0.7s  scale 1 -> 1.22 (35%) -> .93 (65%) -> 1
 *   coinPillGlow   0.9s  glow 0 -> 1 (35%) -> 0
 *   modalIconFly   0.65s
 *   coinFlyUp      0.65s
 */
export function useOneShot(active, duration, easing = Easing.ease) {
  const t = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!active) {
      t.setValue(0);
      return undefined;
    }
    t.setValue(0);
    const anim = Animated.timing(t, {
      toValue: 1,
      duration,
      easing,
      useNativeDriver: NATIVE_DRIVER,
    });
    anim.start();
    return () => anim.stop();
  }, [t, active, duration, easing]);
  return t;
}

/** cubic-bezier(.4,0,.7,1) used by modalIconFly and coinFlyUp. */
export const FLY_EASING = Easing.bezier(0.4, 0, 0.7, 1);
